import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Zap, Trophy, TrendingUp, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, addDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { usePoints } from '@/hooks/usePoints';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface FeaturedSpotlightProps {
  itemType: 'coach' | 'team' | 'venue' | 'player';
  itemId: string;
  teamId?: number;
  showPurchase?: boolean;
}

// Helper to get current user ID and type from either Supabase auth or internal user session
const getCurrentUserInfo = async (): Promise<{ userId: string | null; userType: 'supabase' | 'internal' }> => {
  // First try Supabase auth
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.id) return { userId: user.id, userType: 'supabase' };
  
  // Then try internal user session
  const internalSession = localStorage.getItem('internal_user_session');
  if (internalSession) {
    try {
      const parsed = JSON.parse(internalSession);
      const userId = parsed.userId || parsed.user_id || parsed.id || null;
      if (userId) return { userId, userType: 'internal' };
    } catch {
      // Continue to return null
    }
  }
  
  return { userId: null, userType: 'supabase' };
};

export const FeaturedSpotlight = ({ itemType, itemId, teamId, showPurchase = false }: FeaturedSpotlightProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { pointsData, refetch: refetchPoints } = usePoints();
  const [featuredStatus, setFeaturedStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showInsufficientPoints, setShowInsufficientPoints] = useState(false);
  const [neededPoints, setNeededPoints] = useState(0);
  const [currentPoints, setCurrentPoints] = useState(0);

  useEffect(() => {
    checkFeaturedStatus();
  }, [itemId, teamId]);

  const checkFeaturedStatus = async () => {
    try {
      let query = supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', itemType)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .order('priority', { ascending: false })
        .limit(1);

      if (itemType === 'team' && teamId) {
        query = query.eq('team_id', teamId);
      } else {
        query = query.eq('item_id', itemId);
      }

      const { data, error } = await query.single();

      if (data) {
        setFeaturedStatus(data);
      }
    } catch (error) {
      console.error('Error checking featured status:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseSpotlight = async (duration: 7 | 30, pointsCost: number) => {
    try {
      // Check if user has enough points
      const userPoints = pointsData?.total_points || 0;
      if (userPoints < pointsCost) {
        setNeededPoints(pointsCost);
        setCurrentPoints(userPoints);
        setShowInsufficientPoints(true);
        return;
      }

      setPurchasing(true);
      
      const { userId: currentUserId, userType } = await getCurrentUserInfo();
      
      if (!currentUserId) {
        toast({
          title: "Authentication Required",
          description: "Please log in to activate spotlight features.",
          variant: "destructive",
        });
        return;
      }

      const endDate = addDays(new Date(), duration);

      const insertData: any = {
        item_type: itemType,
        feature_type: 'spotlight',
        priority: duration === 30 ? 2 : 1,
        end_date: endDate.toISOString(),
        paid_promotion: true,
        locker_points_spent: pointsCost,
        created_by: currentUserId
      };

      if (itemType === 'team' && teamId) {
        insertData.team_id = teamId;
      } else {
        insertData.item_id = itemId;
      }

      const { error } = await supabase.from('featured_items').insert(insertData);

      if (error) throw error;

      // Deduct points using spend_points RPC
      const { error: spendError } = await supabase.rpc('spend_points', {
        p_user_id: currentUserId,
        p_user_type: userType,
        p_points: pointsCost,
        p_spend_type: 'spotlight_purchase',
        p_description: `${duration}-day spotlight activation for ${itemType}`,
        p_metadata: { item_type: itemType, duration, item_id: itemId }
      });

      if (spendError) {
        console.error('Error deducting points:', spendError);
        // Don't throw - the spotlight was created successfully
      }

      // Refresh points balance
      refetchPoints();

      toast({
        title: "Spotlight Activated!",
        description: `Your ${itemType} is now featured for ${duration} days. ${pointsCost} points have been deducted.`,
      });

      checkFeaturedStatus();
    } catch (error: any) {
      console.error('Error purchasing spotlight:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to activate spotlight. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPurchasing(false);
    }
  };

  const getFeatureIcon = (type: string) => {
    switch (type) {
      case 'spotlight':
        return <Star className="h-5 w-5" />;
      case 'featured':
        return <Trophy className="h-5 w-5" />;
      case 'highlighted':
        return <Zap className="h-5 w-5" />;
      default:
        return <TrendingUp className="h-5 w-5" />;
    }
  };

  if (loading) {
    return null;
  }

  if (featuredStatus) {
    return (
      <Card className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-yellow-600">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-600/30 rounded-full">
              {getFeatureIcon(featuredStatus.feature_type)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary" className="bg-yellow-600/30 text-yellow-500 border-yellow-600/50">
                  Featured
                </Badge>
                {featuredStatus.priority > 1 && (
                  <Badge variant="secondary" className="bg-orange-600/30 text-orange-500">
                    Premium
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-300">
                Active until {format(new Date(featuredStatus.end_date), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!showPurchase) {
    return null;
  }

  return (
    <>
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Boost Your Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-400">
            Get featured on the homepage and increase your profile views by up to 10x!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-white">7-Day Spotlight</p>
                    <p className="text-xs text-gray-400">Basic feature</p>
                  </div>
                  <Badge variant="secondary">500 pts</Badge>
                </div>
                <ul className="text-xs space-y-1 text-gray-400">
                  <li>✓ Homepage feature</li>
                  <li>✓ Search priority boost</li>
                  <li>✓ Profile badge</li>
                </ul>
                <Button
                  onClick={() => purchaseSpotlight(7, 500)}
                  className="w-full"
                  variant="outline"
                  disabled={purchasing}
                >
                  {purchasing ? 'Activating...' : 'Activate'}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-600/10 to-orange-600/10 border-yellow-600">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-white">30-Day Premium</p>
                    <p className="text-xs text-yellow-500">Best value!</p>
                  </div>
                  <Badge className="bg-yellow-600/30 text-yellow-500 border-yellow-600/50">
                    1500 pts
                  </Badge>
                </div>
                <ul className="text-xs space-y-1 text-gray-400">
                  <li>✓ Premium homepage slot</li>
                  <li>✓ Maximum search boost</li>
                  <li>✓ Golden profile badge</li>
                  <li>✓ Newsletter feature</li>
                </ul>
                <Button
                  onClick={() => purchaseSpotlight(30, 1500)}
                  className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                  disabled={purchasing}
                >
                  {purchasing ? 'Activating...' : 'Activate Premium'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showInsufficientPoints} onOpenChange={setShowInsufficientPoints}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Not Enough Points
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-gray-300">
                You need <span className="font-semibold text-yellow-500">{neededPoints} points</span> to activate this spotlight, 
                but you only have <span className="font-semibold text-white">{currentPoints} points</span>.
              </p>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                <p className="text-sm text-gray-400">Points needed:</p>
                <p className="text-lg font-bold text-yellow-500">{neededPoints - currentPoints} more points</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => navigate('/points')}
              className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
            >
              Earn More Points
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};